import tokens from '@kluein/design-tokens';
import { Box, Button, Text } from '@kluein/klue-ui';
import truncate from 'lodash/truncate';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { Logo } from 'components/common/logo';
import { useAuth } from 'contexts/auth';
import { useRedirect } from 'hooks/use-redirect';
import { APP_V1_BASEURL } from 'lib/urls';
import store from 'store';
import TEST_IDS from 'test-ids';

import type { RivalGroupRivalItemType, RivalType } from 'api/api.types';
import type { RootState } from 'store/store.types';

const StyledProfileBox = styled(Box)`
  &:hover {
    background-color: ${tokens.color.highlight.main};
  }
`;

export type RivalItemProps = RivalGroupRivalItemType & {
  plain?: boolean;
  isSuggestion?: boolean;
  currentProfileId?: number;
  profileButtonTestId?: string;
  battlecardButtonTestId?: string;
  onNavigate: VoidFunction;
};

const RivalItem = (props: RivalItemProps) => {
  const {
    id,
    name,
    iconUrl,
    profileId,
    isSuggestion = false,
    currentProfileId,
    profileButtonTestId = TEST_IDS.layout.rivalItem.rivalProfileButton,
    battlecardButtonTestId = TEST_IDS.layout.rivalItem.rivalBattlecardButton,
    onNavigate,
  } = props;
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation(['Common']);
  const { isCurator, companyLinkDefault, isNewCardEditorEnabled } = useAuth();
  const navigate = useNavigate();
  const { redirectToCompany } = useRedirect();

  const rival = useSelector<RootState, RivalType | undefined>((state) =>
    store.select.rivals.byId(state).get(id.toString()),
  );

  const goToBattlecards = (profileID: number) => {
    if (isCurator && !isNewCardEditorEnabled) {
      setLoading(true); // redirect may take a few seconds, lets indicate the action for users
      return (window.location.href = `${APP_V1_BASEURL}/profile/${profileID}/battlecard/view`);
    }
    navigate(`/profile/${profileID}/battlecard/view`);
    onNavigate();
  };

  const extraButtonProps = {};

  if (isSuggestion) {
    Object.assign(extraButtonProps, {
      as: 'div',
      role: 'button',
    });
  }

  const isSelected = currentProfileId === profileId;
  let backgroundColour = props.plain
    ? 'transparent'
    : tokens.color.neutral.rosegrey.main;

  if (isSelected) {
    backgroundColour = tokens.color.highlight.main;
  }

  return (
    <StyledProfileBox
      direction="row"
      align="center"
      justify="between"
      flex={{ shrink: 0 }}
      background={backgroundColour}
      pad={{ horizontal: 'xsmall' }}
      round="small"
    >
      <Box
        pad={{ vertical: 'xxsmall' }}
        flex="grow"
        border={
          props.plain || companyLinkDefault === 'battlecard'
            ? undefined
            : { side: 'right' }
        }
      >
        <Button
          fill
          variant="plain"
          size="small"
          onClick={() => {
            redirectToCompany(profileId, rival?.supportedRivalId);
            onNavigate();
          }}
          title={name}
          {...extraButtonProps}
          data-test-id={profileButtonTestId}
          leftSlot={
            <Logo
              a11yTitle={name}
              border
              data-test-id={TEST_IDS.layout.rivalItem.rivalLogo}
              size="large"
              src={iconUrl}
            />
          }
        >
          <Text as="label" size="small" fontWeight="semibold">
            <Box direction="row" align="center" gap="xsmall">
              {' '}
              {loading ? 'Loading...' : truncate(name, { length: 18 })}
            </Box>
          </Text>
        </Button>
      </Box>
      {companyLinkDefault !== 'battlecard' && (
        <Box pad={{ left: 'xsmall' }}>
          <Button
            size="small"
            variant="plain"
            title={t('Common:battlecards')}
            onClick={() => {
              goToBattlecards(profileId);
            }}
            {...extraButtonProps}
            data-test-id={battlecardButtonTestId}
          >
            <Text
              as="label"
              fontWeight="semibold"
              size="xsmall"
              color={
                isSelected
                  ? tokens.color.neutral.black.main
                  : tokens.color.neutral.darkgrey.main
              }
            >
              {t('Common:battlecards')}
            </Text>
          </Button>
        </Box>
      )}
    </StyledProfileBox>
  );
};

export default RivalItem;
